import React, { KeyboardEvent, PureComponent } from 'react';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Input, notification } from 'antd';
import { ChatMessage } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import AppContext from 'app/common/context/AppContext';
import { Flex } from 'app/components/common/Flex';
import './CoordinationChat.less';
import { SolutionState } from 'app/model/SOL49';
import Utils from 'app/common/util/Utils';

interface CoordinationChatProps {
    coordinationId: string;
    disabled: boolean;
}

interface CoordinationChatState {
    message: string;
    messages: ChatMessage[];
    canPoll: boolean;
}

export class CoordinationChat extends PureComponent<CoordinationChatProps, CoordinationChatState> {
    private timer;

    constructor(props) {
        super(props);

        this.state = {
            message: '',
            messages: [],
            canPoll: true,
        };
    }

    componentDidMount() {
        this.timer = setInterval(() => this.pollChatMessages(), 5000);
        this.pollChatMessages();
    }

    componentWillUnmount(): void {
        clearInterval(this.timer);
    }

    private pollChatMessages() {
        if (this.state.canPoll) {
            this.setState({ canPoll: false });
            ServiceFactory.sol49Service()
                .getCoordinationChatData(this.props.coordinationId)
                .then(reply => {
                    if (reply.error) {
                        notification.error({ message: reply.error.message, description: reply.error.details });
                    } else {
                        this.setState({
                            messages: reply.data.reverse(),
                        });
                    }
                })
                .catch(error => {
                    notification.error({ message: error });
                })
                .finally(() => this.setState({ canPoll: true }));
        }
    }

    doSendMessage = () => {
        const chatMessage = {
            message: this.state.message,
            fromId: AppContext.configData.claimedUserId,
            toId: this.props.coordinationId,
            timestamp: AppContext.getCurrentTime(),
        };

        this.setState({ canPoll: false });
        ServiceFactory.technicalService()
            .postChatMessage(chatMessage)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: reply.error.message, description: reply.error.details });
                } else {
                    this.setState(state => ({
                        message: '',
                        messages: [reply.data, ...state.messages],
                    }));
                }
            })
            .catch(error => {
                notification.error({ message: error });
            })
            .finally(() => this.setState({ canPoll: true }));
    };

    private handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && Utils.isNotEmptyString(this.state.message.trim())) {
            this.doSendMessage();
        }
    };

    render() {
        return (
            <Flex direction="vertical" className="coordination-chat overflow">
                <Flex size="2em" className="header">
                    COMMENTS
                </Flex>
                <Flex size="5em" className={`footer ${this.props.disabled ? 'disabled' : ''}`}>
                    <Input
                        value={this.state.message}
                        placeholder="Enter your message..."
                        onChange={event => this.setState({ message: event.target.value })}
                        onKeyDown={this.handleKeyPress}
                    />
                </Flex>
                <div className="chat overflow">
                    {this.state.messages.map((chatMessage, idx) => {
                        let isStateUpdate = '';
                        if (chatMessage.message.includes(SolutionState.APPROVED)) {
                            isStateUpdate = 'approved';
                        } else if (chatMessage.message.includes(SolutionState.REJECTED)) {
                            isStateUpdate = 'rejected';
                        } else if (chatMessage.message.includes(SolutionState.ACKNOWLEDGED)) {
                            isStateUpdate = 'ack';
                        }
                        const isFmp = chatMessage.fromId.includes('fmp');
                        const fmpMessageClassname = isFmp ? 'fmp' : '';
                        const myMessageClassname =
                            chatMessage.fromId === AppContext.configData.claimedUserId ? 'mine' : '';
                        const chatMessageClassname = `chat-message ${fmpMessageClassname} ${myMessageClassname}`;
                        const messageClassname = `message ${isStateUpdate}`;
                        const fromId = (
                            <>{`${isFmp ? Utils.toUpperCase(chatMessage.fromId) : chatMessage.fromId + ' (NM)'}`}</>
                        );
                        return (
                            <div key={idx} className={chatMessageClassname}>
                                <Flex className="title">
                                    <span className="timestamp">{TimeUtils.formatHHMM(chatMessage.timestamp)}</span>
                                    <span className="from">{fromId}</span>
                                </Flex>
                                <div className={messageClassname}>{chatMessage.message}</div>
                            </div>
                        );
                    })}
                </div>
            </Flex>
        );
    }
}
